import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import GameScreenLayout from "@/components/GameScreenLayout"
import { getShopItems, purchaseShopItem } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { formatShopReward } from "@/constants/gameMeta"

export default function ShopPage() {
  const queryClient = useQueryClient()
  const { passport, syncGameState } = useGuardiansPassport()

  const itemsQuery = useQuery({
    queryKey: ["shop-items"],
    queryFn: getShopItems,
  })

  const purchase = useMutation({
    mutationFn: (itemId: string) => purchaseShopItem(itemId),
    onSuccess: async () => {
      await syncGameState()
      void queryClient.invalidateQueries({ queryKey: ["shop-items"] })
    },
  })

  const items = itemsQuery.data?.items ?? []

  return (
    <GameScreenLayout title="Магазин">
      <p className="mb-4 text-sm text-guardians-muted">
        Монеты: <span className="text-guardians-accent">{passport?.coins ?? 0}</span>
      </p>

      {itemsQuery.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 p-3"
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm">{item.title}</div>
                {item.description ? (
                  <div className="text-xs text-guardians-muted">{item.description}</div>
                ) : null}
                <div className="mt-1 text-xs text-guardians-accent">
                  {formatShopReward(item.rewardType, item.rewardAmount)}
                </div>
              </div>
              <button
                type="button"
                disabled={purchase.isPending}
                onClick={() => purchase.mutate(item.id)}
                className="shrink-0 rounded-lg bg-guardians-accent px-3 py-2 text-xs font-medium text-guardians-bg disabled:opacity-60"
              >
                {item.priceCoins} 🪙
              </button>
            </li>
          ))}
        </ul>
      )}

      {purchase.isError ? (
        <p className="mt-3 text-sm text-red-400">
          {getGameApiErrorMessage(purchase.error, "Ошибка покупки")}
        </p>
      ) : null}
    </GameScreenLayout>
  )
}
